import { pdf } from '@react-pdf/renderer'
import { unwrapFieldValue } from 'features/libra/ontology/ontologyFieldDefaults'
import {
  type FieldValues,
  type OntologyField,
  stripComponents,
} from 'features/libra/ontology/ontologyFieldTypes'
import type {
  SignoffConfig,
  SignoffsField,
} from 'features/libra/signoff/signoffTypes'
import { buildNiiReportContent } from 'features/libra/stepComponents/niiReport/content/buildNiiReportContent'
import type {
  NiiTicketLifecycle,
  NiiZoneImage,
} from 'features/libra/stepComponents/niiReport/content/niiReportContentTypes'
import type { NiiReportFields } from 'features/libra/stepComponents/niiReport/niiReportApi'
import type { ZoneParams } from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import type { LibraWorkflowStep } from 'features/libra/workflow/workflowStepsApi'
import { createElement, type ReactElement } from 'react'

import { NiiReportPdfDocument } from './NiiReportPdfDocument'

/**
 * Filename for a generated NII report PDF. `version`, when given, is
 * appended so a batch of reports for the same asset (bulk export, which has
 * no zip to keep them apart) don't collide on disk; the single-ticket export
 * omits it since only one file is ever saved at a time.
 */
export function buildNiiReportDownloadFilename(
  siteName: string | undefined,
  assetName: string | undefined,
  version?: number
): string {
  const parts = [
    siteName,
    assetName,
    version === undefined ? 'NII-Report' : `NII-Report-v${version}`,
  ].filter(Boolean)
  return `${parts.join('-')}.pdf`
}

export interface BuildNiiReportPdfBlobArgs {
  workflowSteps: ReadonlyArray<
    Pick<LibraWorkflowStep, 'id' | 'name' | 'component' | 'config' | 'branch'>
  >
  fieldValues: FieldValues
  ontologyFields: Record<string, OntologyField>
  zones: ReadonlyArray<ZoneParams>
  zoneImages: ReadonlyArray<NiiZoneImage>
  reportFields: NiiReportFields
  /**
   * Slot definitions for the closing review section; undefined omits its
   * sign-off table.
   */
  signoffConfig: SignoffConfig | undefined
  signoffs: SignoffsField
  lifecycle: NiiTicketLifecycle
  siteName: string | undefined
  assetName: string | undefined
  analysisVersion: number
}

/**
 * Pure PDF assembly for an NII report: builds the step sections from ticket
 * data and renders them to a `Blob`. No hooks or Context — every input is an
 * explicit argument — so this is shared by the single-ticket export button
 * (`useNiiPdfExport`, ticket-scoped via Context) and the dashboard's bulk
 * export (`useBulkNiiPdfExport`, which has no single ticket in scope).
 */
export async function buildNiiReportPdfBlob({
  workflowSteps,
  fieldValues,
  ontologyFields,
  zones,
  zoneImages,
  reportFields,
  signoffConfig,
  signoffs,
  lifecycle,
  siteName,
  assetName,
  analysisVersion,
}: BuildNiiReportPdfBlobArgs): Promise<Blob> {
  const ticketFieldValues = stripComponents(fieldValues)

  const steps = buildNiiReportContent(workflowSteps, {
    fieldValues: ticketFieldValues,
    ontologyFields,
    zones,
    zoneImages,
    reportFields,
    signoffConfig,
    signoffs,
    lifecycle,
  })

  // `pdf()` types its argument as a `<Document>` element, but what's passed
  // here (as before this was extracted) is a component that *renders* one.
  // JSX hid the mismatch because `JSX.Element` has `any` props;
  // `createElement` infers the component's own props, so the widening has to
  // be explicit. Same cast as `useAstReportExport.ts`, the other non-JSX
  // caller of `pdf()`.
  const document = createElement(NiiReportPdfDocument, {
    chrome: {
      siteName,
      assetName,
      analysisDate: unwrapFieldValue<string>(
        ticketFieldValues[reportFields.analysisDate]
      ),
      analysisVersion,
    },
    steps,
  }) as ReactElement

  return pdf(document).toBlob()
}
